// ContextCard — ONE data-driven surface for "what grounds this conversation",
// pure. Promoted from sketches/context-card (commit 017c7d9d).
//
// THE IDEA: the hero composer's empty "add context" invitation and the chat
// thread's populated "here's what's grounding this turn" block are the SAME
// component, fed different data. ChatGPT's attachment-card row is the reference
// — a compact tile per item (icon, title, one-line subtitle, a remove ×) plus a
// quiet "add more" entry — reused for BOTH the empty invitation and the
// populated block.
//
// The two visual states are NOT a variant prop — they fall out of the data
// (`items.length === 0` vs not), so a half-populated card is unrepresentable.
// Only the SURROUNDING frame differs between hero and thread placement, and
// that framing lives in the CALLER (see ConversationContextCard for the thread
// placement, WorkspaceLanding for the hero placement), never in this component.
//
// The "＋ add" affordance opens the SAME picker vocabulary the composer's `@`
// mention trigger surfaces — `pickMenuItems` is exported and reused by both, so
// `@` is a keyboard shortcut INTO this card's command, not a separate feature.
//
// Pure/props-driven: no state, no I/O, no dates — every item, every callback
// arrives via props. `disabled` freezes every affordance (remove, upload,
// pick).

import { Trans, useLingui } from "@lingui/react/macro";
import { AiMarker } from "../AiMarker/AiMarker.js";
import type { ComposerResourceRef } from "../Composer/PowerComposer/types.js";
import { Icon } from "../Icon/Icon.js";
import type { MenuItem } from "../Menu/Menu.js";
import { Menu } from "../Menu/Menu.js";
import { Text } from "../Text/Text.js";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden.js";
import {
    type ContextItem,
    contextItemGlyph,
    contextItemId,
    isSourceInContext,
    kindGlyph,
} from "./types.js";
import "./ContextCard.css";

// The PROPS CONTRACT AppShellHost will supply in the follow-up wiring:
//   · items    — the resolved refs + in-flight uploads grounding the turn
//   · pickable — the mentionable resource pool the picker offers (the SAME
//                list PowerComposer takes as `candidates`)
//   · onUpload — open the file picker (host wires it to ingest)
//   · onPick   — a pickable source was chosen (host introduces it to context)
//   · onRemove — drop one item by id (host drops it from context / cancels an
//                in-flight upload)
//   · disabled — freeze every affordance (whole surface inert)
export type ContextCardProps = {
    readonly items: readonly ContextItem[];
    readonly pickable: readonly ComposerResourceRef[];
    readonly onUpload: () => void;
    readonly onPick: (source: ComposerResourceRef) => void;
    readonly onRemove: (id: string) => void;
    readonly disabled?: boolean;
    // Whether the card owns its own add affordances (the invite entrances and
    // the populated "＋" menu). Default true (the hero/thread placements). The
    // ContextSideCard sets it false: THAT card owns a richer add path (the "+"
    // reveals a fuzzy reference search over sources AND learning contexts), so
    // here the card is DISPLAY-ONLY — grounded tiles + remove, no second adder.
    readonly showAdd?: boolean;
};

// The picker rows shared by "Choose existing" (invitation state), the populated
// state's "＋" menu, AND the composer's `@`-mention listbox — one command, many
// entrances, proven by literally sharing this row builder. Already-grounded
// sources are DISABLED with a check glyph rather than omitted (error
// prevention, Nielsen #5).
export const pickMenuItems = <Ref extends ComposerResourceRef>(
    pickable: readonly Ref[],
    items: readonly ContextItem[],
    onPick: (source: Ref) => void,
): readonly MenuItem[] =>
    pickable.map((source) => {
        const already = isSourceInContext(items, source.id);
        return {
            _tag: "item",
            label: source.title,
            icon: (
                <Icon
                    name={already ? "check" : kindGlyph(source.kind)}
                    size="xs"
                />
            ),
            disabled: already,
            onSelect: () => onPick(source),
        };
    });

const uploadGlyph = <Icon name="upload" size="xs" />;
const plusGlyph = <Icon name="plus" size="xs" />;

// The populated state's "＋" menu: upload leads (it needs no prior candidate
// list), then a separator, then the same pick rows the invitation and the `@`
// listbox share. Precomputed labels keep this helper plain strings in,
// `MenuItem[]` out.
const addMenuItems = (
    uploadLabel: string,
    nothingLabel: string,
    onUpload: () => void,
    pickItems: readonly MenuItem[],
): readonly MenuItem[] => [
    { _tag: "item", label: uploadLabel, icon: uploadGlyph, onSelect: onUpload },
    { _tag: "separator" },
    ...(pickItems.length === 0
        ? [{ _tag: "label", label: nothingLabel } satisfies MenuItem]
        : pickItems),
];

export const ContextCard = ({
    items,
    pickable,
    onUpload,
    onPick,
    onRemove,
    disabled = false,
    showAdd = true,
}: ContextCardProps) => {
    const { t } = useLingui();

    const pickItems = pickMenuItems(pickable, items, onPick);

    // The item's one-line subtitle: the transient phase while in-flight, else
    // the resource kind noun. Closes over `t` (the lingui macro is only valid
    // as a tagged template at a literal call site).
    const subtitleFor = (item: ContextItem): string => {
        if (item._tag === "upload") {
            return item.upload.phase === "uploading"
                ? t`Uploading…`
                : t`Processing…`;
        }
        switch (item.ref.kind) {
            case "source":
                return t`Source`;
            case "material":
                return t`Material`;
            case "package":
                return t({ message: "Package", context: "resource kind" });
            case "file":
                return t`File`;
        }
    };

    const titleFor = (item: ContextItem): string =>
        item._tag === "upload" ? item.upload.filename : item.ref.title;

    return (
        <div
            className="bilda-context-card"
            data-empty={items.length === 0 || undefined}
            aria-disabled={disabled || undefined}
        >
            {items.length === 0 && !showAdd ? (
                // DISPLAY-ONLY empty (the ContextSideCard owns adding): a quiet
                // resting hint, never a blank region.
                <Text as="p" size="sm" tone="dim">
                    <Trans>Nothing grounding this yet.</Trans>
                </Text>
            ) : items.length === 0 ? (
                // INVITATION — both entrances stated plainly (recognition over
                // recall, Nielsen #6): no menu to open just to learn the card
                // does two things.
                <>
                    <button
                        type="button"
                        className="bilda-context-card__invite"
                        disabled={disabled}
                        onClick={onUpload}
                    >
                        {uploadGlyph}
                        <Text as="span" size="sm">
                            <Trans>Upload a file</Trans>
                        </Text>
                    </button>
                    {/* "Choose existing" is HIDDEN when there is nothing to pick
                        (A-02 C3 — never a dead affordance that opens onto a
                        "Nothing to pick yet" dead-end). Upload is the only door
                        on a fresh workspace; the pick door appears once the
                        library holds something. */}
                    {pickItems.length > 0 ? (
                        <Menu
                            trigger={
                                <button
                                    type="button"
                                    className="bilda-context-card__invite"
                                    disabled={disabled}
                                >
                                    <Icon name="search" size="xs" />
                                    <Text as="span" size="sm">
                                        <Trans>Choose existing</Trans>
                                    </Text>
                                </button>
                            }
                            items={pickItems}
                        />
                    ) : null}
                </>
            ) : (
                // POPULATED — the ChatGPT-card row: a tile per item, then one
                // quiet "＋" that folds BOTH entrances back into a menu
                // (progressive disclosure — once something is grounded, adding
                // more is a secondary action, not the headline).
                <>
                    {items.map((item) => {
                        const id = contextItemId(item);
                        const title = titleFor(item);
                        return (
                            <span
                                key={id}
                                className="bilda-context-card__tile"
                                data-pending={
                                    item._tag === "upload" || undefined
                                }
                            >
                                <span
                                    className="bilda-context-card__glyph"
                                    aria-hidden="true"
                                >
                                    <Icon
                                        name={contextItemGlyph(item)}
                                        size="sm"
                                    />
                                </span>
                                <span className="bilda-context-card__body">
                                    <Text
                                        as="span"
                                        size="sm"
                                        weight="medium"
                                        className="bilda-context-card__title"
                                    >
                                        {title}
                                    </Text>
                                    <span className="bilda-context-card__sub">
                                        <Text as="span" size="xs" tone="dim">
                                            {subtitleFor(item)}
                                        </Text>
                                        {item._tag === "resource" &&
                                        item.ref.aiAuthored === true ? (
                                            <AiMarker variant="tag" />
                                        ) : null}
                                    </span>
                                </span>
                                {disabled ? null : (
                                    <button
                                        type="button"
                                        className="bilda-context-card__remove"
                                        onClick={() => onRemove(id)}
                                    >
                                        <VisuallyHidden>
                                            <Trans>Remove {title}</Trans>
                                        </VisuallyHidden>
                                        <Icon name="close" size="xs" />
                                    </button>
                                )}
                            </span>
                        );
                    })}
                    {showAdd ? (
                        <Menu
                            trigger={
                                <button
                                    type="button"
                                    className="bilda-context-card__add"
                                    disabled={disabled}
                                >
                                    <VisuallyHidden>
                                        <Trans>Add context</Trans>
                                    </VisuallyHidden>
                                    {plusGlyph}
                                </button>
                            }
                            items={addMenuItems(
                                t`Upload a file`,
                                t`Nothing else to pick`,
                                onUpload,
                                pickItems,
                            )}
                        />
                    ) : null}
                </>
            )}
        </div>
    );
};
